import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import DataTableExtensions from "react-data-table-component-extensions";
import DataTable from "react-data-table-component";

import { tableCustomStyles } from "app/mock/catalog";

import "styles/main.css";
import "react-data-table-component-extensions/dist/index.css";
import { PageWrapper } from "components/ui/PageWrapper";
import { Edit2, Trash2, Key } from "lucide-react";
import { Button } from "components/ui/Button";
import { FormInput } from "components/ui/FormInput";
import { ButtonLoader } from "components/Loader/ButtonLoader";
import { ComponentLoader } from "components/Loader/ComponentLoader";
import { ErrorComponent } from "components/Alerts/Error";
import { InfoComponent } from "components/Alerts/Info";
import { GetAllAdminMutation } from "rest/admin";
import { DeleteAdminMutation } from "rest/admin";
import { UpdateAdminPasswordMutation } from "rest/admin";

export default function AllAdmin() {
  const navigate = useNavigate();
  const [id, setId] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const passwordSchema = yup.object({
    oldPassword: yup
      .string()
      .required("Old password is required")
      .min(6, "Old password must be at least 6 characters"),
    newPassword: yup
      .string()
      .required("New password is required")
      .min(8, "New password must be at least 8 characters"),
    confirmPassword: yup
      .string()
      .required("Confirm password is required")
      .oneOf([yup.ref("newPassword"), null], "Passwords must match"),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(passwordSchema) });

  const allAdmin = GetAllAdminMutation();
  const deleteAdmin = DeleteAdminMutation();
  const updatePasswordMutation = UpdateAdminPasswordMutation();

  const handleDelete = (id) => {
    deleteAdmin.mutate(id);
  };

  const handleOpenPasswordModal = (row) => {
    setSelectedAdmin(row);
    reset({ oldPassword: "", newPassword: "", confirmPassword: "" });
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedAdmin(null);
  };

  const onSubmitUpdatePassword = (data) => {
    if (!selectedAdmin?.id) return;
    updatePasswordMutation.mutate(
      {
        adminId: selectedAdmin.id,
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword,
      },
      {
        onSuccess: () => {
          handleClosePasswordModal();
        },
      }
    );
  };

  const routesData = [
    {
      name: "Name",
      selector: (row) => row.name,
      grow: 2,
    },
    {
      name: "email",
      selector: (row) => row.email,
      grow: 3,
    },
    {
      name: "Update Pas...",
      cell: (row) => (
        <span
          className="editbtn"
          onClick={() => handleOpenPasswordModal(row)}
          title="Update Password"
        >
          <Key size={16} />
        </span>
      ),
      button: true.toString(),
      ignoreRowClick: true,
      allowOverflow: true,
      width: "110px",
      style: {},
    },
    // {
    //   name: "edit",
    //   cell: (row) => (
    //     <span
    //       className="editbtn"
    //       onClick={() => navigate(`/edit-routes/${row?.id}`)}
    //     >
    //       <Edit2 row={row} />
    //     </span>
    //   ),
    //   button: true.toString(),
    //   style: {},
    // },
    {
      name: "Delete",
      cell: (row) =>
        row?.id === id && deleteAdmin?.isPending ? (
          <ButtonLoader />
        ) : (
          <span
            className="deletebtn"
            onClick={() => {
              handleDelete(row?.id);
              setId(row?.id);
            }}
          >
            <Trash2 row={row} />
          </span>
        ),
      button: true.toString(),
      ignoreRowClick: true,
      allowOverflow: true,
      width: "90px",
      style: {},
    },
  ];
  return (
    <>
      <PageWrapper slug="all-admin" name="All Admin" />
      {allAdmin?.isError ? (
        <ErrorComponent message="OOPS ! something went wrong" />
      ) : null}

      {allAdmin?.isPending ? (
        <ComponentLoader />
      ) : (
        <>
          <div className="d-flex justify-content-end mb-4 add_catalog_btn mt-4">
            <Button onClick={() => navigate("/add-admin")}>Add Admin</Button>
          </div>
          {allAdmin?.data?.data?.length >= 1 ? (
            <div style={{ maxWidth: "900px" }}>
              <DataTableExtensions
                columns={routesData}
                data={allAdmin?.data?.data}
                filterPlaceholder="Search"
              >
                <DataTable
                  pagination
                  paginationPerPage={10}
                  striped
                  dense
                  customStyles={{
                    ...tableCustomStyles,
                    rows: {
                      style: { minHeight: "38px" },
                    },
                    headCells: {
                      style: { paddingTop: "8px", paddingBottom: "8px", fontSize: "0.9rem" },
                    },
                    cells: {
                      style: { paddingTop: "8px", paddingBottom: "8px" },
                    },
                  }}
                />
              </DataTableExtensions>
            </div>
          ) : (
            <InfoComponent message={"Please Add Data to Display"} />
          )}
        </>
      )}
      {showPasswordModal ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div className="card" style={{ width: "520px", maxWidth: "90%" }}>
            <div className="card-body">
              <h5 className="mb-3">Update Password {selectedAdmin?.email ? `- ${selectedAdmin.email}` : ""}</h5>
              <form onSubmit={handleSubmit(onSubmitUpdatePassword)}>
                <div className="mb-3">
                  <label className="form-label" htmlFor="oldPassword">
                    Old Password
                  </label>
                  <FormInput
                    type="password"
                    name="oldPassword"
                    placeholder="Enter old password"
                    {...register("oldPassword")}
                  />
                  {errors?.oldPassword ? (
                    <p className="errorMessage">{errors?.oldPassword?.message}</p>
                  ) : null}
                </div>

                <div className="mb-3">
                  <label className="form-label" htmlFor="newPassword">
                    New Password
                  </label>
                  <FormInput
                    type="password"
                    name="newPassword"
                    placeholder="Enter new password"
                    {...register("newPassword")}
                  />
                  {errors?.newPassword ? (
                    <p className="errorMessage">{errors?.newPassword?.message}</p>
                  ) : null}
                </div>

                <div className="mb-3">
                  <label className="form-label" htmlFor="confirmPassword">
                    Confirm Password
                  </label>
                  <FormInput
                    type="password"
                    name="confirmPassword"
                    placeholder="Re-enter new password"
                    {...register("confirmPassword")}
                  />
                  {errors?.confirmPassword ? (
                    <p className="errorMessage">{errors?.confirmPassword?.message}</p>
                  ) : null}
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <Button type="button" onClick={handleClosePasswordModal}>
                    Cancel
                  </Button>
                  {updatePasswordMutation?.isPending ? (
                    <Button type="button">
                      <ButtonLoader />
                    </Button>
                  ) : (
                    <Button type="submit">Update</Button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
